import React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";

import pick from "ramda/src/pick";
import ifElse from "ramda/src/ifElse";
import identity from "ramda/src/identity";
import mergeAll from "ramda/src/mergeAll";

import FileCopyIcon from "@mui/icons-material/FileCopy";

import { Modal } from "../../../components/Modal";
import { Button } from "../../../UI/MUIButton";
import { normalize } from "../../../helpers/normalize";
import { useModalContext } from "../../../contexts/ModalContext/context";
import { ChipMultipleSelect } from "../../../components/ChipMultipleSelect";

import { NetworkInfo } from "./types";
import { useNetworksToCopy } from "./useNetworksToCopy";
import { EntrataNetworkMappings } from "../../../services/IntegrationApi";
import { useSettingsToCopy, DATE_TYPE, ENTRATA_END_DATE, ENTRATA_START_DATE, ALLOW_ACCESS } from "./useSettingsToCopy";

interface IProps {
  row: NetworkInfo;
  networkMappings: NetworkInfo[];
  nameOptions: Record<string, string>;
  setNetworkMapping: React.Dispatch<React.SetStateAction<EntrataNetworkMappings[]>>;
}

export const EntrataMappingRowSettingsCopy: React.FC<IProps> = (props) => {
  const { setIsOpen } = useModalContext();
  const { networksToCopy, handleNetworkChange } = useNetworksToCopy();
  const { settingsToCopy, getSettingsChecked, handleSettingsChange } = useSettingsToCopy();

  const handleUpdate = () => {
    const copyingValues = pick(settingsToCopy, props.row);

    const handleNetworkUpdate = ifElse(
      (network: NetworkInfo) => networksToCopy.includes(network.rowId as number),
      (network: NetworkInfo) =>
        mergeAll([
          network,
          copyingValues,
          {
            updated: true,
            valid: !!(
              network.l1Id &&
              network.entrataFloorPlan &&
              network.entrataStartDate &&
              network.entrataEndDate &&
              network.dateType &&
              network.entrataUnitSpace
            ),
          },
        ]),
      identity
    );

    const updatedNetworks = props.networkMappings.map(handleNetworkUpdate);

    props.setNetworkMapping(updatedNetworks as any);

    setIsOpen(false);
  };

  const getNetworName = React.useCallback(
    (row: NetworkInfo) => {
      return row.l4Id
        ? props.nameOptions[`${row.l1Id}-${row.l2Id}-${row.l3Id}-${row.l4Id}`]
        : props.nameOptions[`${row.l1Id}-${row.l2Id}-${row.l3Id}`];
    },
    [normalize(props.nameOptions)]
  );

  const options = React.useMemo(
    () =>
      props.networkMappings
        .filter((network) => network.rowId !== props.row.rowId)
        .map((network) => ({
          id: network.rowId as number,
          name: getNetworName(network),
        })),
    [normalize(props.networkMappings)]
  );

  const handleSettingsCopy = () => setIsOpen(true);

  return (
    <>
      <IconButton onClick={handleSettingsCopy}>
        <FileCopyIcon />
      </IconButton>
      <Modal
        maxWidth="sm"
        title="Copy Row Settings to Other Rows"
        actions={
          <Button color="primary" variant="outlined" onClick={handleUpdate}>
            Update
          </Button>
        }
      >
        <Box p={3}>
          <Typography gutterBottom>
            You are copying settings from <strong>{getNetworName(props.row)}</strong>
          </Typography>
          <br />
          <FormControl component="fieldset">
            <FormLabel component="legend">Settings to copy</FormLabel>
            <FormGroup row>
              <FormControlLabel
                label="Lease Start Date"
                control={
                  <Checkbox
                    color="primary"
                    name={DATE_TYPE}
                    checked={getSettingsChecked(DATE_TYPE)}
                    onChange={handleSettingsChange(DATE_TYPE)}
                  />
                }
              />
              <FormControlLabel
                label="Date range start"
                control={
                  <Checkbox
                    color="primary"
                    name={ENTRATA_START_DATE}
                    checked={getSettingsChecked(ENTRATA_START_DATE)}
                    onChange={handleSettingsChange(ENTRATA_START_DATE)}
                  />
                }
              />
              <FormControlLabel
                label="Date range end"
                control={
                  <Checkbox
                    color="primary"
                    name={ENTRATA_END_DATE}
                    checked={getSettingsChecked(ENTRATA_END_DATE)}
                    onChange={handleSettingsChange(ENTRATA_END_DATE)}
                  />
                }
              />
              <FormControlLabel
                label="Enabled"
                control={
                  <Checkbox
                    color="primary"
                    name={ALLOW_ACCESS}
                    checked={getSettingsChecked(ALLOW_ACCESS)}
                    onChange={handleSettingsChange(ALLOW_ACCESS)}
                  />
                }
              />
            </FormGroup>
          </FormControl>
          <ChipMultipleSelect
            options={options}
            value={networksToCopy}
            label="L2-L4 to be updated"
            handleChange={handleNetworkChange}
          />
        </Box>
      </Modal>
    </>
  );
};
